import { buildBrandContext } from "@faire/prototype-playground/components/shared/brand-home-framework/brandContext";
import { type HomeModule } from "@faire/prototype-playground/components/shared/brand-home-framework/moduleTypes";
import { renderModule } from "@faire/prototype-playground/components/shared/brand-home-framework/renderModule";
import { cn } from "@faire/prototype-playground/lib/utils";
import type { PrototypeMetadata } from "@faire/prototype-playground/workspace/types";
import React, { useEffect, useState } from "react";

import { getBrandDataset, PF_BRAND_TOKEN } from "./data";
import { FrameBox } from "./framework/FrameBox";

/** Standard breakpoints the framework designs to. `null` = full width. */
const BREAKPOINTS: { label: string; value: number | null }[] = [
  { label: "Full", value: null },
  { label: "Mobile", value: 375 },
  { label: "Tablet", value: 768 },
  { label: "Desktop", value: 1280 },
];

export const prototypeMeta: PrototypeMetadata = {
  name: "Brand Home — Module Library",
  owner: "Ryan Lee",
  email: "ryan.lee@faire.com",
  status: "in-progress",
  platform: "web",
  createdAt: "2026-08-08",
  lastUpdated: "2026-08-08",
  surfaceArea: "Retailer",
  category: "experimental",
  creationTool: "claude-code",
  thumbnail: "/images/thumbnails/rl-brand-home-modules.png",
};

/** A library entry: the declarative module + how to describe it in the sheet.
 * The `module` objects here are the exact same shape the live home composes. */
interface LibraryEntry {
  id: string;
  name: string;
  category:
    | "Header"
    | "Merchandising"
    | "Social proof"
    | "Content"
    | "Conversion";
  description: string;
  module: HomeModule;
}

const ENTRIES: LibraryEntry[] = [
  {
    id: "header-redesigned",
    name: "Header — Redesigned masthead",
    category: "Header",
    description:
      "Maroon hero: brand identity, rating meta, value-prop band + hero image. Logged-out shows MSRP + a sign-up band.",
    module: { kind: "header", variant: "redesigned" },
  },
  {
    id: "header-current",
    name: "Header — Current cover header",
    category: "Header",
    description:
      "Production brand-page header: full-bleed cover, avatar + name, Insider shipping row, Follow / message / overflow.",
    module: { kind: "header", variant: "current" },
  },
  {
    id: "category-tiles",
    name: "Category tiles",
    category: "Merchandising",
    description:
      "Circular category shortcuts. One flexed row on desktop, a swipeable row on mobile.",
    module: { kind: "categories" },
  },
  {
    id: "featured-collection",
    name: "Featured collection",
    category: "Merchandising",
    description:
      "50/50 image + product grid to feature one curated collection. Stacks on mobile.",
    module: {
      kind: "collection",
      title: "Tried & True Best Sellers",
      subtitle: "These fragrances are our best sellers for a reason!",
      select: "triedAndTrue",
    },
  },
  {
    id: "product-row",
    name: "Product row (grid)",
    category: "Merchandising",
    description:
      "A multi-row product grid — 6-up desktop, 2-up mobile — with an optional 'Shop all' action.",
    module: {
      kind: "productRow",
      title: "Top reordered products",
      select: "bestsellers",
      rows: 2,
    },
  },
  {
    id: "product-carousel",
    name: "Product carousel",
    category: "Merchandising",
    description: "A horizontally scrollable product row.",
    module: {
      kind: "productCarousel",
      title: "Bestsellers",
      select: "bestsellers",
    },
  },
  {
    id: "collections-row",
    name: "Collections row",
    category: "Merchandising",
    description: "Hero-image collection tiles linking to curated groups.",
    module: { kind: "collectionsRow" },
  },
  {
    id: "editorial-banner",
    name: "Editorial banner",
    category: "Content",
    description: "Full-bleed editorial hero with a headline and CTA.",
    module: { kind: "editorial" },
  },
  {
    id: "referral-banner",
    name: "Referral banner",
    category: "Conversion",
    description:
      "Inset incentive card (logged-out): 50% off first order + a year of free shipping.",
    module: { kind: "referral" },
  },
  {
    id: "highly-rated",
    name: "Highly rated",
    category: "Social proof",
    description: "Product tiles, each with a glowing 5-star review quote.",
    module: { kind: "highlyRated" },
  },
  {
    id: "reviews",
    name: "Ratings & reviews",
    category: "Social proof",
    description: "Rating score + star distribution + individual review cards.",
    module: { kind: "reviews" },
  },
  {
    id: "video",
    name: "Behind the scenes (video)",
    category: "Content",
    description:
      "Brand video carousel with title + date captions below each card.",
    module: { kind: "video" },
  },
  {
    id: "about",
    name: "About",
    category: "Content",
    description:
      "Full-bleed cream block: avatar, socials, brand story, value tags, and an Information table.",
    module: { kind: "about" },
  },
];

const ModuleGalleryPage: React.FC = () => {
  useEffect(() => {
    document.title = prototypeMeta.name;
  }, []);

  const [bp, setBp] = useState<number | null>(null);

  // The framework seam: one BrandContext, resolved from a brand token. Swap the
  // token and every module below re-renders as that brand.
  const ctx = buildBrandContext(getBrandDataset(1), {
    brandToken: PF_BRAND_TOKEN,
    isLoggedOut: true,
  });

  return (
    <div className="bg-fs-surface-primary min-h-screen">
      <div className="border-fs-border-muted bg-fs-surface-primary sticky top-0 z-20 border-b">
        <div className="mx-auto flex max-w-[1200px] items-center gap-2 px-4 py-3">
          <span className="type-fs-label text-fs-text-subdued mr-1">
            Breakpoint
          </span>
          {BREAKPOINTS.map((breakpoint) => (
            <button
              key={breakpoint.label}
              onClick={() => setBp(breakpoint.value)}
              className={cn(
                "type-fs-label rounded-full border px-3 py-1.5",
                bp === breakpoint.value
                  ? "border-fs-text-primary bg-fs-surface-secondary text-fs-text-primary"
                  : "border-fs-border-muted text-fs-text-subdued"
              )}
            >
              {breakpoint.value
                ? `${breakpoint.label} · ${breakpoint.value}`
                : breakpoint.label}
            </button>
          ))}
        </div>
      </div>
      <div className="mx-auto max-w-[1200px] px-4 pt-10 pb-6">
        <h1 className="type-fs-display-s text-fs-text-primary">
          Brand Home — Module Library
        </h1>
        <p className="type-fs-paragraph text-fs-text-subdued mt-2 max-w-2xl">
          Every content module the brand-home framework composes, rendered from
          the shared registry with P.F. Candle sample data. The live home builds
          itself from these exact same module objects.
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          {ENTRIES.map((entry) => (
            <a
              key={entry.id}
              href={`#${entry.id}`}
              className="type-fs-label border-fs-border-muted text-fs-text-primary rounded-full border px-3 py-1.5"
            >
              {entry.name}
            </a>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-14 pb-24">
        {ENTRIES.map((entry) => (
          <section key={entry.id} id={entry.id} className="scroll-mt-6">
            <div className="mx-auto mb-3 max-w-[1200px] px-4">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h2 className="type-fs-section-heading text-fs-text-primary">
                  {entry.name}
                </h2>
                <span className="type-fs-label text-fs-text-subdued bg-fs-surface-secondary rounded-full px-2.5 py-0.5">
                  {entry.category}
                </span>
              </div>
              <p className="type-fs-paragraph text-fs-text-subdued mt-1 max-w-2xl">
                {entry.description}
              </p>
            </div>
            <div
              className={cn(
                "border-fs-border-muted border-y",
                bp === null
                  ? "bg-fs-surface-primary overflow-hidden"
                  : "bg-fs-surface-secondary px-4 py-8"
              )}
            >
              {bp === null ? (
                renderModule(entry.module, ctx)
              ) : (
                <FrameBox key={`${entry.id}-${bp}`} width={bp}>
                  {renderModule(entry.module, ctx)}
                </FrameBox>
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default ModuleGalleryPage;
